import { typography, radius } from '@/shared/theme/tokens';
import { LIVE_STATUS_META, withAlpha } from '../../liveModeMeta';
import { BCAST, tickerDurationMs } from './broadcastMeta';

// Self-contained: pause the crawl on hover so a moving segment is clickable.
const TICKER_CSS = `.bc-ticker-track:hover { animation-play-state: paused; }`;

/**
 * The bottom crawl — the single broadcast ticker carrying every alert:
 * PARAMETER · STATUS · VALUE. Stale items grey out and read "——" (never a
 * fabricated number). The crawl is the only ambient motion on the screen and is
 * gated on `animate`: under reduced-motion it renders static. The alert that
 * just changed (`flashId`) flashes once. Each segment is a click target →
 * onSelect(alert).
 */
export function TickerRibbon({ alerts = [], flashId, animate, onSelect }) {
  const clickable = typeof onSelect === 'function';

  return (
    <div style={{
      flexShrink: 0, display: 'flex', alignItems: 'stretch', height: 52,
      background: BCAST.panel, borderTop: `2px solid ${BCAST.lineBright}`, overflow: 'hidden',
    }}>
      <style>{TICKER_CSS}</style>

      {/* Channel category tag at the head of the crawl */}
      <div style={{
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px',
        background: BCAST.live, color: BCAST.studio, zIndex: 2,
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>notifications_active</span>
        <span style={{
          fontFamily: BCAST.heading, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.extrabold,
          letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase',
        }}>
          Live Alerts
        </span>
      </div>

      {/* Crawl */}
      <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        {alerts.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 18px' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 18, color: BCAST.ok }}>check_circle</span>
            <span style={{
              fontFamily: BCAST.mono, fontSize: typography.fontSize.md, color: BCAST.inkDim,
              letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase',
            }}>
              All parameters in range
            </span>
          </div>
        ) : animate ? (
          <div
            className="bc-ticker-track"
            style={{
              display: 'flex', alignItems: 'center', flexShrink: 0, whiteSpace: 'nowrap', willChange: 'transform',
              animation: `bc-marquee ${tickerDurationMs(alerts.length)}ms linear infinite`,
            }}
          >
            <Segments alerts={alerts} flashId={flashId} animate clickable={clickable} onSelect={onSelect} />
            <Segments alerts={alerts} flashId={flashId} animate clickable={clickable} onSelect={onSelect} ariaHidden />
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
            <Segments alerts={alerts} flashId={flashId} animate={false} clickable={clickable} onSelect={onSelect} />
          </div>
        )}
      </div>
    </div>
  );
}

function Segments({ alerts, flashId, animate, clickable, onSelect, ariaHidden }) {
  return (
    <div aria-hidden={ariaHidden || undefined} style={{ display: 'flex', alignItems: 'center' }}>
      {alerts.map((a) => {
        const stale = !!a.isStale;
        const meta = stale ? null : (LIVE_STATUS_META[a.status] ?? LIVE_STATUS_META.ok);
        const color = stale ? BCAST.inkFaint : meta.color;
        const flashing = animate && !stale && a.id === flashId;

        return (
          <div
            key={`${ariaHidden ? 'b' : 'a'}-${a.id}`}
            onClick={clickable ? () => onSelect(a) : undefined}
            onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(a); } } : undefined}
            role={clickable && !ariaHidden ? 'button' : undefined}
            tabIndex={clickable && !ariaHidden ? 0 : undefined}
            title={clickable ? `Open ${a.parameterName} alert` : undefined}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 9, padding: '0 18px', height: 52,
              borderRight: `1px solid ${BCAST.line}`,
              background: flashing ? withAlpha(color, '2e') : 'transparent',
              opacity: stale ? 0.55 : 1,
              cursor: clickable ? 'pointer' : 'default',
              transition: animate ? 'background 0.5s ease' : 'none',
            }}
          >
            <span style={{
              width: 8, height: 8, borderRadius: radius.full, background: color, flexShrink: 0,
              boxShadow: flashing ? `0 0 8px ${color}` : 'none',
            }} />
            <span style={{
              fontFamily: BCAST.heading, fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold,
              letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', color: BCAST.ink,
            }}>
              {a.parameterName}
            </span>
            <span style={{
              fontFamily: BCAST.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold,
              letterSpacing: typography.letterSpacing.wide, textTransform: 'uppercase', color,
            }}>
              {stale ? 'STALE' : (LIVE_STATUS_META[a.status] ?? LIVE_STATUS_META.ok).label}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 3 }}>
              <span style={{
                fontFamily: BCAST.mono, fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold,
                color, fontVariantNumeric: 'tabular-nums',
              }}>
                {stale ? '——' : a.liveValue}
              </span>
              {a.unit && !stale && (
                <span style={{ fontFamily: BCAST.mono, fontSize: typography.fontSize.xs, color: BCAST.inkFaint }}>{a.unit}</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
